import { Component } from '@theme/component';

const desktop = window.matchMedia('(min-width: 990px)');

class MarylunaCollectionComponent extends Component {
  #observer = null;
  #loading = false;
  #controller = null;
  #current = window.location.pathname + window.location.search;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('click', this.#onDocumentClick);
    window.addEventListener('popstate', this.#onPopState);
    desktop.addEventListener('change', this.#onBreakpoint);
    this.#observeMore();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('click', this.#onDocumentClick);
    window.removeEventListener('popstate', this.#onPopState);
    desktop.removeEventListener('change', this.#onBreakpoint);
    this.#observer?.disconnect();
    this.#controller?.abort();
  }

  openFilters() {
    const dialog = this.querySelector('.ml-plp__filters');
    if (dialog.open) return;
    dialog.showModal();
    document.documentElement.setAttribute('scroll-lock', '');
    dialog.addEventListener('close', () => document.documentElement.removeAttribute('scroll-lock'), { once: true });
  }

  closeFilters() {
    if (this.querySelector('.ml-plp__filters').open) this.querySelector('.ml-plp__filters').close();
  }

  onDialogClick(event) {
    if (event.composedPath()[0] === this.querySelector('.ml-plp__filters')) this.closeFilters();
  }

  onFilterChange(event) {
    if (event.composedPath()[0].type === 'number') return;
    if (desktop.matches) this.#render(this.#urlFromForm());
  }

  applyFilters(event) {
    event.preventDefault();
    this.closeFilters();
    this.#render(this.#urlFromForm());
  }

  clearFilters(event) {
    event.preventDefault();
    this.closeFilters();
    const url = new URL(window.location.href);
    const sort = url.searchParams.get('sort_by');
    this.#render(`${url.pathname}${sort ? `?sort_by=${encodeURIComponent(sort)}` : ''}`);
  }

  sort(event) {
    const form = this.querySelector('.ml-plp__filter-form');
    form.elements.sort_by.value = event.target.dataset.sort;
    event.target.closest('details')?.removeAttribute('open');
    this.#render(this.#urlFromForm());
  }

  #urlFromForm() {
    const params = new URLSearchParams();
    for (const [key, value] of new FormData(this.querySelector('.ml-plp__filter-form'))) {
      if (value !== '') params.append(key, value);
    }
    return `${window.location.pathname}?${params}`;
  }

  async #render(url, push = true) {
    this.#controller?.abort();
    const controller = (this.#controller = new AbortController());
    this.#loading = false;
    this.querySelector('.ml-plp__results')?.setAttribute('aria-busy', 'true');
    try {
      const html = await this.#fetchSection(url, controller.signal);
      const next = html.querySelector('maryluna-collection-component');
      if (!next) throw Object.assign(new Error('Missing section'), { name: 'HttpError' });
      const focused = document.activeElement;
      const focusSelector = this.contains(focused) && focused.name ? `[name="${CSS.escape(focused.name)}"][value="${CSS.escape(focused.value)}"]` : null;
      this.replaceChildren(...next.childNodes);
      if (focusSelector) this.querySelector(focusSelector)?.focus();
      if (push) history.pushState({ maryluna: true }, '', url);
      this.#current = window.location.pathname + window.location.search;
      this.#observeMore();
    } catch (error) {
      if (error.name === 'HttpError') window.location.assign(url);
    } finally {
      if (!controller.signal.aborted) this.querySelector('.ml-plp__results')?.removeAttribute('aria-busy');
    }
  }

  async #fetchSection(url, signal) {
    const target = new URL(url, window.location.origin);
    target.searchParams.set('section_id', this.dataset.sectionId);
    const response = await fetch(target, { signal });
    if (!response.ok) throw Object.assign(new Error(response.statusText), { name: 'HttpError' });
    return new DOMParser().parseFromString(await response.text(), 'text/html');
  }

  #observeMore() {
    this.#observer?.disconnect();
    const more = this.querySelector('.ml-plp__more');
    if (!more) return;
    this.#observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) this.#loadMore(more);
    }, { rootMargin: '600px 0px' });
    this.#observer.observe(more);
    more.addEventListener('click', (event) => {
      event.preventDefault();
      this.#loadMore(more);
    }, { once: true });
  }

  async #loadMore(more) {
    if (this.#loading) return;
    this.#loading = true;
    this.#controller ??= new AbortController();
    const { signal } = this.#controller;
    try {
      const html = await this.#fetchSection(more.dataset.nextUrl, signal);
      if (!more.isConnected) return;
      this.querySelector('.ml-plp__grid')?.append(...html.querySelectorAll('.ml-plp__grid > li'));
      const nextMore = html.querySelector('.ml-plp__more');
      if (nextMore) {
        more.replaceWith(nextMore);
      } else {
        more.remove();
      }
    } catch (error) {
      if (error.name === 'HttpError' && more.isConnected) window.location.assign(more.dataset.nextUrl);
      return;
    } finally {
      if (!signal.aborted) this.#loading = false;
    }
    this.#observeMore();
  }

  #onDocumentClick = (event) => {
    for (const dropdown of this.querySelectorAll('details[data-dropdown][open]')) {
      if (!dropdown.contains(event.target)) dropdown.removeAttribute('open');
    }
  };

  #onBreakpoint = () => {
    if (desktop.matches) this.closeFilters();
  };

  #onPopState = () => {
    if (window.location.pathname + window.location.search === this.#current) return;
    this.#render(window.location.href, false);
  };
}

if (!customElements.get('maryluna-collection-component')) {
  customElements.define('maryluna-collection-component', MarylunaCollectionComponent);
}
